import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Price } from '@/components/product/Price';
import { BrandArtwork, COLOURWAYS, type ColourwayId } from '@/components/brand/BrandArtwork';
import { ColourwaySwatches } from '@/components/product/ColourwaySwatches';
import type { ProductCard as ProductCardType } from '@/types/catalogue';

/**
 * Product card — BUILD-PLAN §9, §44, §62, §74.
 *
 * The whole card is one link rather than a card containing several links:
 * nested interactive elements produce duplicated tab stops and a confusing
 * screen-reader experience. Badges and metadata are plain text inside it.
 *
 * The hover treatment is deliberately restrained — a slow image rise and a
 * thin rule drawing under the title. §5 warns against cheap animation; the
 * luxury signal is slowness and precision, not movement for its own sake.
 */

interface ProductCardProps {
  product: ProductCardType;
  /**
   * Priority images skip lazy loading. Set for the first row only — lazily
   * loading an above-the-fold image delays LCP, which is the opposite of
   * what §28 asks for.
   */
  priority?: boolean;
}

export function ProductCard({ product, priority = false }: ProductCardProps) {
  /*
   * Default finish is derived from the product name, so a given product keeps
   * a consistent look across the grid and between visits — but the customer
   * can override it from the card without opening the product.
   */
  const [colourway, setColourway] = useState<ColourwayId>(
    () => defaultColourwayFor(product.name),
  );

  /*
   * A flex column, so every card in a row ends at the same place.
   * -------------------------------------------------------------------------
   * Grid cells already stretch to equal height, but the CONTENT inside them
   * did not: a card carrying a "Personalisable" line pushed its swatches lower
   * than its neighbours', so a row of individually correct cards still looked
   * ragged. The meta block grows to absorb the difference, which puts the
   * swatches on one line across the whole row.
   */
  return (
    <article className="group flex h-full flex-col">
      <Link
        /*
         | The chosen finish travels in the URL rather than in memory, so it
         | survives the navigation, the back button, a refresh and a shared
         | link. Holding it in component state would silently drop the
         | customer's choice the moment they opened the product.
         */
        to={`/products/${product.slug}?finish=${colourway}`}
        className="flex flex-1 flex-col rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
      >
        {/*
          Fixed aspect ratio so the grid never reflows as images arrive —
          the layout-shift half of Core Web Vitals (§28).
        */}
        {/*
          Depth, and a reason for the eye to rest.
          --------------------------------------------------------------------
          The card was a flat rectangle with a flat image in it. The brief
          asked for layered cards with real depth — so the frame carries a
          shadow that deepens on hover, and the artwork itself scales very
          slightly inside its overflow-hidden frame. The movement is small on
          purpose: a card that leaps reads as a web page, a card that settles
          reads as a shop.
        */}
        <div className="relative aspect-square overflow-hidden bg-surface-muted shadow-card transition-shadow duration-300 group-hover:shadow-raised sm:aspect-3/4">
          {product.image ? (
            <img
              src={product.image.url}
              alt={product.image.alt}
              loading={priority ? 'eager' : 'lazy'}
              {...(priority ? { fetchPriority: 'high' as const } : {})}
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
            />
          ) : (
            <div className="h-full w-full transition-transform duration-[900ms] ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100">
              <BrandArtwork seed={product.name} ratio="portrait" colourway={colourway} />
            </div>
          )}

          {/* Warm scrim on hover, so the label below reads as a considered
              reveal rather than a tooltip. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-linear-to-t from-charcoal/25 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 motion-reduce:transition-none"
          />

          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 pb-5 text-center text-[0.625rem] uppercase tracking-[0.3em] text-white opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100 motion-reduce:transition-none"
          >
            View Piece
          </span>

          {/* At most one badge, so the image is never crowded (§5). */}
          {!product.inStock ? (
            <Badge tone="muted">Out of Stock</Badge>
          ) : product.onSale ? (
            <Badge tone="sale">Sale</Badge>
          ) : product.isBestseller ? (
            <Badge tone="brand">Bestseller</Badge>
          ) : null}
        </div>

        <div className="mt-3.5 flex flex-1 flex-col gap-1 sm:mt-4 sm:gap-1.5">
          {/* Clamped to two lines. A long product name wrapping to three
              made neighbouring cards in the same row disagree on where the
              price sat, which is the detail that makes a grid look untidy. */}
          <h3 className="line-clamp-2 min-h-[2.6em] font-display text-[0.9375rem] leading-snug text-ink sm:text-lg">
            <span className="bg-linear-to-r from-accent to-accent bg-size-[0%_1px] bg-bottom-left bg-no-repeat pb-0.5 transition-[background-size] duration-500 group-hover:bg-size-[100%_1px] motion-reduce:transition-none">
              {product.name}
            </span>
          </h3>

          <Price price={product.price} compareAt={product.compareAtPrice} size="sm" />

          {product.hasPersonalisation && (
            <span className="mt-1 inline-flex items-center gap-1.5 text-[0.6875rem] uppercase tracking-[0.16em] text-accent">
              <Sparkles className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" />
              Personalisable
            </span>
          )}
        </div>
      </Link>

      {/*
        Outside the <Link> deliberately. Interactive controls nested inside a
        link produce invalid HTML, steal the click, and give screen readers a
        link containing buttons (§40). Only shown when the product has no
        photography — with real images the gallery drives the finish instead.
      */}
      {!product.image && (
        <div className="mt-2.5 sm:mt-3">
          <ColourwaySwatches
            value={colourway}
            onChange={setColourway}
            size="sm"
            showLabel={false}
          />
        </div>
      )}
    </article>
  );
}

/** Stable per-product default, matching BrandArtwork's own seeding. */
function defaultColourwayFor(name: string): ColourwayId {
  let value = 5381;
  for (let i = 0; i < name.length; i++) {
    value = ((value << 5) + value + name.charCodeAt(i)) >>> 0;
  }

  return COLOURWAYS[value % COLOURWAYS.length]!.id;
}

function Badge({ children, tone }: { children: React.ReactNode; tone: 'sale' | 'brand' | 'muted' }) {
  const tones = {
    sale: 'bg-danger text-white',
    brand: 'bg-accent text-white',
    muted: 'bg-ink/85 text-white',
  } as const;

  return (
    <span
      className={`absolute left-4 top-4 px-3 py-1.5 text-[0.5625rem] uppercase tracking-[0.2em] ${tones[tone]}`}
    >
      {children}
    </span>
  );
}
